import * as React from "react";
import {
	RiAlignCenter,
	RiAlignJustify,
	RiAlignLeft,
	RiAlignRight,
	RiCodeSSlashLine,
	RiLink,
	RiLinkUnlink,
	RiTaskLine,
} from "react-icons/ri";

export const blobToBase64 = async (blob) => {
	return await new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.readAsDataURL(blob);
		reader.onload = () => resolve(reader.result);
		reader.onerror = (error) => reject(error);
	});
};

function ToolbarDivider() {
	return <div className="mx-0.5 hidden h-6 w-px shrink-0 bg-darkBlue/15 sm:block" aria-hidden />;
}

function ToolbarGroup({ label, children }) {
	return (
		<div className="flex flex-wrap items-center gap-0.5 rounded-[10px] bg-white/80 p-1 ring-1 ring-darkBlue/8">
			<span className="sr-only">{label}</span>
			{children}
		</div>
	);
}

export const MenuItem = (props) => {
	const { icon, title, action, isActive = null, type = "button", disabled = false } = props;

	const baseClass =
		"flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px] border-0 transition disabled:opacity-40";
	const activeClass = isActive?.()
		? "bg-darkBlue text-white shadow-sm"
		: "bg-transparent text-darkBlue/75 hover:bg-lightCta hover:text-darkBlue";

	if (type === "upload") {
		return (
			<div className={`relative ${baseClass} ${activeClass}`}>
				<label
					className="absolute inset-0 flex cursor-pointer items-center justify-center"
					htmlFor={`upload-${title}`}
					title={title}
				>
					<svg className="h-[18px] w-[18px] fill-current" aria-hidden>
						<use href={`/icons/remixicon-symbol.svg#ri-${icon}`} />
					</svg>
				</label>
				<input
					id={`upload-${title}`}
					type="file"
					accept="image/*"
					className="sr-only"
					onChange={(e) => {
						const file = e.target?.files?.[0];
						if (file) {
							blobToBase64(file).then((src) => {
								src && action?.(src);
							});
						}
						e.target.value = "";
					}}
				/>
			</div>
		);
	}

	if (type === "text") {
		return (
			<button
				type="button"
				className={`${baseClass} min-w-[2rem] px-1 font-Vazir-700 text-[13px] underline ${activeClass}`}
				onClick={action}
				title={title}
				disabled={disabled}
			>
				{icon}
			</button>
		);
	}

	if (type === "icon") {
		const Icon = icon;
		return (
			<button
				type="button"
				className={`${baseClass} ${activeClass}`}
				onClick={action}
				title={title}
				disabled={disabled}
			>
				<Icon className="h-[18px] w-[18px]" aria-hidden />
			</button>
		);
	}

	return (
		<button
			type="button"
			className={`${baseClass} ${activeClass}`}
			onClick={action}
			title={title}
			disabled={disabled}
		>
			<svg className="h-[18px] w-[18px] fill-current" aria-hidden>
				<use href={`/icons/remixicon-symbol.svg#ri-${icon}`} />
			</svg>
		</button>
	);
};

function setLink(editor) {
	const previousUrl = editor.getAttributes("link").href;
	const url = window.prompt("آدرس لینک (https://…)", previousUrl || "https://");
	if (url === null) return;
	if (url === "") {
		editor.chain().focus().extendMarkRange("link").unsetLink().run();
		return;
	}
	editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
}

export const MenuBar = ({ editor }) => {
	const [, setRevision] = React.useState(0);

	React.useEffect(() => {
		if (!editor) return undefined;
		const bump = () => setRevision((n) => n + 1);
		editor.on("transaction", bump);
		editor.on("selectionUpdate", bump);
		return () => {
			editor.off("transaction", bump);
			editor.off("selectionUpdate", bump);
		};
	}, [editor]);

	const canUndo = editor.can().chain().focus().undo().run();
	const canRedo = editor.can().chain().focus().redo().run();

	const items = React.useMemo(
		() => ({
			history: [
				{
					icon: "arrow-go-back-line",
					title: "بازگشت (Undo)",
					action: () => editor.chain().focus().undo().run(),
					disabled: !canUndo,
				},
				{
					icon: "arrow-go-forward-line",
					title: "از نو (Redo)",
					action: () => editor.chain().focus().redo().run(),
					disabled: !canRedo,
				},
			],
			headings: [
				{
					icon: "paragraph",
					title: "متن عادی",
					action: () => editor.chain().focus().setParagraph().run(),
					isActive: () => editor.isActive("paragraph") && !editor.isActive("heading"),
				},
				{
					icon: "h-1",
					title: "تیتر بزرگ (H1)",
					action: () => editor.chain().focus().toggleHeading({ level: 1 }).run(),
					isActive: () => editor.isActive("heading", { level: 1 }),
				},
				{
					icon: "h-2",
					title: "تیتر متوسط (H2)",
					action: () => editor.chain().focus().toggleHeading({ level: 2 }).run(),
					isActive: () => editor.isActive("heading", { level: 2 }),
				},
				{
					icon: "h-3",
					title: "تیتر کوچک (H3)",
					action: () => editor.chain().focus().toggleHeading({ level: 3 }).run(),
					isActive: () => editor.isActive("heading", { level: 3 }),
				},
			],
			inline: [
				{
					icon: "bold",
					title: "درشت",
					action: () => editor.chain().focus().toggleBold().run(),
					isActive: () => editor.isActive("bold"),
				},
				{
					icon: "italic",
					title: "کج",
					action: () => editor.chain().focus().toggleItalic().run(),
					isActive: () => editor.isActive("italic"),
				},
				{
					icon: "strikethrough",
					title: "خط‌خورده",
					action: () => editor.chain().focus().toggleStrike().run(),
					isActive: () => editor.isActive("strike"),
				},
				{
					type: "text",
					icon: "U",
					title: "زیرخط",
					action: () => editor.chain().focus().toggleUnderline().run(),
					isActive: () => editor.isActive("underline"),
				},
				{
					icon: "mark-pen-line",
					title: "هایلایت",
					action: () => editor.chain().focus().toggleHighlight().run(),
					isActive: () => editor.isActive("highlight"),
				},
			],
			lists: [
				{
					icon: "list-unordered",
					title: "لیست نقطه‌ای",
					action: () => editor.chain().focus().toggleBulletList().run(),
					isActive: () => editor.isActive("bulletList"),
				},
				{
					icon: "list-ordered",
					title: "لیست شماره‌دار",
					action: () => editor.chain().focus().toggleOrderedList().run(),
					isActive: () => editor.isActive("orderedList"),
				},
				{
					type: "icon",
					icon: RiTaskLine,
					title: "چک‌لیست",
					action: () => editor.chain().focus().toggleTaskList().run(),
					isActive: () => editor.isActive("taskList"),
				},
				{
					icon: "double-quotes-l",
					title: "نقل‌قول",
					action: () => editor.chain().focus().toggleBlockquote().run(),
					isActive: () => editor.isActive("blockquote"),
				},
				{
					type: "icon",
					icon: RiCodeSSlashLine,
					title: "بلوک کد",
					action: () => editor.chain().focus().toggleCodeBlock().run(),
					isActive: () => editor.isActive("codeBlock"),
				},
			],
			align: [
				{
					type: "icon",
					icon: RiAlignRight,
					title: "راست‌چین",
					action: () => editor.chain().focus().setTextAlign("right").run(),
					isActive: () => editor.isActive({ textAlign: "right" }),
				},
				{
					type: "icon",
					icon: RiAlignCenter,
					title: "وسط‌چین",
					action: () => editor.chain().focus().setTextAlign("center").run(),
					isActive: () => editor.isActive({ textAlign: "center" }),
				},
				{
					type: "icon",
					icon: RiAlignLeft,
					title: "چپ‌چین",
					action: () => editor.chain().focus().setTextAlign("left").run(),
					isActive: () => editor.isActive({ textAlign: "left" }),
				},
				{
					type: "icon",
					icon: RiAlignJustify,
					title: "تمام‌عرض",
					action: () => editor.chain().focus().setTextAlign("justify").run(),
					isActive: () => editor.isActive({ textAlign: "justify" }),
				},
			],
			insert: [
				{
					type: "icon",
					icon: RiLink,
					title: "درج لینک",
					action: () => setLink(editor),
					isActive: () => editor.isActive("link"),
				},
				{
					type: "icon",
					icon: RiLinkUnlink,
					title: "حذف لینک",
					action: () => editor.chain().focus().unsetLink().run(),
				},
				{
					icon: "separator",
					title: "خط جداکننده",
					action: () => editor.chain().focus().setHorizontalRule().run(),
				},
				{
					type: "upload",
					icon: "image-2-fill",
					title: "درج تصویر",
					action: (url) => editor.chain().focus().setImage({ src: url }).run(),
				},
			],
		}),
		[editor, canRedo, canUndo]
	);

	const renderGroup = (group) =>
		group.map((item, index) => <MenuItem key={`${item.title}-${index}`} {...item} />);

	return (
		<div
			className="sticky top-0 z-10 border-b border-darkBlue/10 bg-[#F8FAFD] px-2 py-2 md:px-3"
			dir="rtl"
		>
			<div className="flex flex-wrap items-center gap-2">
				<ToolbarGroup label="تاریخچه">{renderGroup(items.history)}</ToolbarGroup>
				<ToolbarDivider />
				<ToolbarGroup label="تیتر">{renderGroup(items.headings)}</ToolbarGroup>
				<ToolbarDivider />
				<ToolbarGroup label="قالب">{renderGroup(items.inline)}</ToolbarGroup>
				<ToolbarDivider />
				<ToolbarGroup label="لیست">{renderGroup(items.lists)}</ToolbarGroup>
				<ToolbarDivider />
				<ToolbarGroup label="تراز">{renderGroup(items.align)}</ToolbarGroup>
				<ToolbarDivider />
				<ToolbarGroup label="درج">{renderGroup(items.insert)}</ToolbarGroup>
			</div>
			<p className="mt-2 hidden font-Vazir-400 text-[11px] text-darkBlue/45 md:block">
				میانبر: تصویر را بکشید و رها کنید یا Ctrl+V — لینک را از نوار «درج» اضافه کنید.
			</p>
		</div>
	);
};
